import React, { useState, useMemo } from 'react';
import { useTheme, Theme } from '../contexts/ThemeContext';

// RN-ALIGNED: Clean createStyles function matching React Native pattern
const createStyles = (theme: Theme) => ({
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    overflowY: 'auto' as const,
    height: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    // WEB-SPECIFIC: Responsive padding for small screens
    padding: `${theme.spacing.md}px`,
    zIndex: 50,
    transition: `opacity ${theme.motion.duration.normal}ms ${theme.motion.easing.standard}`,
    backgroundColor: theme.colors.modalOverlay,
    opacity: 1,
    pointerEvents: 'auto' as const,
  },
  overlayHidden: {
    backgroundColor: 'transparent',
    opacity: 0,
    pointerEvents: 'none' as const,
  },
  modal: {
    backgroundColor: theme.colors.modalBackground,
    borderRadius: theme.radii.lg,
    // WEB-SPECIFIC: Responsive padding - smaller on small screens
    padding: window.innerWidth < 480 ? theme.spacing.lg : theme.spacing.xl,
    width: '100%',
    // WEB-SPECIFIC: Dynamic maxWidth calculation for small screens
    maxWidth: Math.min(448, window.innerWidth - (theme.spacing.md * 2)), // 28rem = 448px
    transition: `all ${theme.motion.duration.normal}ms ${theme.motion.easing.standard} transform`,
    opacity: 1,
    transform: 'scale(1)',
    boxShadow: theme.shadows.modal,
  },
  modalHidden: {
    opacity: 0,
    transform: 'scale(0.95)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.xl,
  },
  title: {
    // WEB-SPECIFIC: Responsive font size
    fontSize: window.innerWidth < 480 ? theme.typography.sizes.lg : theme.typography.sizes.xl,
    fontWeight: theme.typography.weights.semiBold,
    color: theme.colors.text,
    margin: 0,
    flex: 1,
    // WEB-SPECIFIC: Better line height for long text on small screens
    lineHeight: theme.typography.lineHeights.normal,
  },
  helpButton: {
    // WEB-SPECIFIC: Slightly smaller on very small screens
    width: window.innerWidth < 480 ? 24 : 28,
    height: window.innerWidth < 480 ? 24 : 28,
    borderRadius: window.innerWidth < 480 ? theme.radii.xl : 14,
    backgroundColor: theme.colors.primary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: theme.spacing.md,
    border: 'none',
    cursor: 'pointer',
    transition: `opacity ${theme.motion.duration.fast}ms ${theme.motion.easing.standard}`,
  },
  helpButtonHover: {
    backgroundColor: theme.colors.buttonPrimaryHover,
  },
  helpText: {
    color: theme.colors.textInverse,
    fontSize: window.innerWidth < 480 ? theme.typography.sizes.md : theme.typography.sizes.lg,
    fontWeight: theme.typography.weights.bold,
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing.md,
    marginBottom: theme.spacing.lg,
  },
  button: {
    // WEB-SPECIFIC: Responsive padding
    paddingLeft: window.innerWidth < 480 ? theme.spacing.md : theme.spacing.lg,
    paddingRight: window.innerWidth < 480 ? theme.spacing.md : theme.spacing.lg,
    paddingTop: theme.spacing.md,
    paddingBottom: theme.spacing.md,
    fontSize: theme.typography.sizes.md,
    fontWeight: theme.typography.weights.medium,
    borderRadius: theme.radii.md,
    border: 'none',
    cursor: 'pointer',
    transition: `all ${theme.motion.duration.fast}ms ${theme.motion.easing.standard}`,
    outline: 'none',
    backgroundColor: theme.colors.surfaceSecondary,
    color: theme.colors.text,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // WEB-SPECIFIC: Minimum touch target for mobile
    minHeight: 44,
  },
  buttonHover: {
    backgroundColor: theme.colors.surfaceTertiary,
  },
  cancelButton: {
    color: theme.colors.textSecondary,
    marginTop: theme.spacing.sm,
  },
  cancelButtonHover: {
    backgroundColor: theme.colors.surfaceSecondary || theme.colors.surface,
    borderColor: theme.colors.textTertiary,
  },
  helpContent: {
    backgroundColor: theme.colors.modalBackground,
    borderRadius: theme.radii.lg,
    // WEB-SPECIFIC: Responsive padding - smaller on small screens
    padding: window.innerWidth < 480 ? theme.spacing.lg : theme.spacing.xl,
    width: '100%',
    // WEB-SPECIFIC: Dynamic maxWidth calculation for help content on small screens
    maxWidth: Math.min(384, window.innerWidth - (theme.spacing.md * 2)), // 24rem = 384px
    transition: `all ${theme.motion.duration.normal}ms ${theme.motion.easing.standard} transform`,
    opacity: 1,
    transform: 'scale(1)',
    boxShadow: theme.shadows.modal,
  },
  helpTitle: {
    // WEB-SPECIFIC: Responsive font size
    fontSize: window.innerWidth < 480 ? theme.typography.sizes.lg : theme.typography.sizes.xl,
    fontWeight: theme.typography.weights.semiBold,
    color: theme.colors.text,
    marginBottom: theme.spacing.lg,
    margin: 0,
    lineHeight: theme.typography.lineHeights.normal,
  },
  helpSection: {
    marginBottom: theme.spacing.lg,
  },
  helpLabel: {
    fontWeight: theme.typography.weights.semiBold,
    color: theme.colors.text,
    fontSize: theme.typography.sizes.md,
    marginBottom: theme.spacing.xs,
    display: 'block' as const,
  },
  helpDescription: {
    color: theme.colors.textSecondary,
    fontSize: theme.typography.sizes.md,
    lineHeight: theme.typography.lineHeights.normal,
    marginBottom: theme.spacing.xs,
  },
  helpExample: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textTertiary,
    fontStyle: 'italic' as const,
    lineHeight: theme.typography.lineHeights.normal,
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.surfaceSecondary || theme.colors.surface,
    borderRadius: theme.radii.sm,
    borderLeft: `3px solid ${theme.colors.primary}`,
    marginTop: theme.spacing.xs,
    // WEB-SPECIFIC: Better text wrapping on small screens
    wordWrap: 'break-word' as const,
    overflowWrap: 'break-word' as const,
  },
  closeHelpButton: {
    marginTop: theme.spacing.xl,
    // WEB-SPECIFIC: Responsive padding
    paddingLeft: window.innerWidth < 480 ? theme.spacing.md : theme.spacing.lg,
    paddingRight: window.innerWidth < 480 ? theme.spacing.md : theme.spacing.lg,
    paddingTop: theme.spacing.md,
    paddingBottom: theme.spacing.md,
    backgroundColor: theme.colors.primary,
    borderRadius: theme.radii.md,
    border: 'none',
    cursor: 'pointer',
    width: '100%',
    transition: `opacity ${theme.motion.duration.fast}ms ${theme.motion.easing.standard}`,
    outline: 'none',
    // WEB-SPECIFIC: Minimum touch target for mobile
    minHeight: 44,
  },
  closeHelpButtonHover: {
    backgroundColor: theme.colors.buttonPrimaryHover,
  },
  closeHelpText: {
    color: theme.colors.textInverse,
    fontWeight: theme.typography.weights.medium,
    fontSize: theme.typography.sizes.md,
  },
});

interface KeyChangeMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectMethod: (method: 'relative' | 'transpose') => void;
  fromKey: string;
  toKey: string;
}

const KeyChangeMethodModal: React.FC<KeyChangeMethodModalProps> = ({
  isOpen,
  onClose,
  onSelectMethod,
  fromKey,
  toKey,
}) => {
  const { theme } = useTheme();
  const [showHelp, setShowHelp] = useState(false);
  // WEB-SPECIFIC: State for hover effects similar to RN Pressable
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);
  const [hoveredHelp, setHoveredHelp] = useState(false);
  const [hoveredCloseHelp, setHoveredCloseHelp] = useState(false);
  
  const styles = useMemo(() => createStyles(theme), [theme]);

  // WEB-SPECIFIC: Combining styles for button states
  const getButtonStyle = (buttonType: string) => ({
    ...styles.button,
    ...(buttonType === 'cancel' ? styles.cancelButton : {}),
    ...(hoveredButton === buttonType ? 
      (buttonType === 'cancel' ? styles.cancelButtonHover : styles.buttonHover) 
      : {})
  });

  const getHelpButtonStyle = () => ({
    ...styles.helpButton,
    ...(hoveredHelp ? styles.helpButtonHover : {})
  });

  const getCloseHelpButtonStyle = () => ({
    ...styles.closeHelpButton,
    ...(hoveredCloseHelp ? styles.closeHelpButtonHover : {})
  });

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      if (showHelp) {
        setShowHelp(false);
      } else {
        onClose();
      }
    }
  };

  const handleMethodSelect = (method: 'relative' | 'transpose') => {
    onSelectMethod(method);
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={handleOverlayClick}>
      {showHelp ? (
        // Help Content
        <div
          style={styles.helpContent}
          onClick={(e) => e.stopPropagation()}
        >
          <h3 style={styles.helpTitle}>Key Change Methods</h3>
          
          <div style={styles.helpSection}>
            <div style={styles.helpLabel}>Relative:</div>
            <div style={styles.helpDescription}>
              Keep chord names the same, but change their role in the new key.
            </div>
            <div style={styles.helpExample}>
              Example: C(I), G(V) → C(IV), G(I)
            </div>
          </div>
          
          <div style={styles.helpSection}>
            <div style={styles.helpLabel}>Transpose:</div>
            <div style={styles.helpDescription}>
              Keep chord roles the same, but change chord names to match the new key.
            </div>
            <div style={styles.helpExample}>
              Example: C(I), G(V) → G(I), D(V)
            </div>
          </div>

          <button
            style={getCloseHelpButtonStyle()}
            onClick={() => setShowHelp(false)}
            onMouseEnter={() => setHoveredCloseHelp(true)}
            onMouseLeave={() => setHoveredCloseHelp(false)}
          >
            <span style={styles.closeHelpText}>Got it</span>
          </button>
        </div>
      ) : (
        // Main Modal Content
        <div
          style={styles.modal}
          onClick={(e) => e.stopPropagation()}
        >
                    <div style={styles.header}>
            <h2 style={styles.title}>
               How would you like to change the key?
            </h2>
            <button
              style={getHelpButtonStyle()}
              onClick={() => setShowHelp(true)}
              onMouseEnter={() => setHoveredHelp(true)}
              onMouseLeave={() => setHoveredHelp(false)}
            >
              <span style={styles.helpText}>?</span>
            </button>
          </div>

          <div style={styles.buttonContainer}>
            <button
              style={getButtonStyle('relative')}
              onClick={() => handleMethodSelect('relative')}
              onMouseEnter={() => setHoveredButton('relative')}
              onMouseLeave={() => setHoveredButton(null)}
            >
              Relative
            </button>

            <button
              style={getButtonStyle('transpose')}
              onClick={() => handleMethodSelect('transpose')}
              onMouseEnter={() => setHoveredButton('transpose')}
              onMouseLeave={() => setHoveredButton(null)}
            >
              Transpose
            </button>

            <button
              style={getButtonStyle('cancel')}
              onClick={onClose}
              onMouseEnter={() => setHoveredButton('cancel')}
              onMouseLeave={() => setHoveredButton(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default KeyChangeMethodModal;
